import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Button,
  Input,
  Modal,
  notification,
  Radio,
  Select,
  Space,
  Table,
} from 'antd';
import { ze } from 'utils/zhEn';
import { upsertServerAppSettings } from 'reducers/app/appActions';
import { ServerAppSettings } from 'utils/server-app-settings';
import {
  extractTeamUuid,
  findDuplicateTeamRowKeys,
  parseTeamOptions,
  parseTeamRows,
  toTeamCsvText,
} from 'utils/jira-team-options';

/**
 * 嵌套在提单弹窗里的「配置Team选项」弹窗。
 * 两种编辑模式：表格（默认，逐行填团队名 / UUID）与 CSV（批量导入导出）。
 * antd4 的 Select 下拉 z-index 是 1050，比 Modal 的 1000 高，所以这里必须显式抬高 zIndex。
 */
export const JIRA_TEAM_OPTIONS_MODAL_Z_INDEX = 1100;

const MODE_TABLE = 'TABLE';
const MODE_CSV = 'CSV';

const PLACEHOLDER = [
  'Team Name,Team UUID',
  'Client Team,36885b3c-1bf0-4f85-a357-c5b858c31de4',
  'Server Team,7a1f0c22-9d3e-4b8a-91c7-2f5e6a8d1b03',
].join('\n');

/** 行级校验：空行不标红（视为"待填写"），但保存时会拦住 */
function getRowError(row) {
  const hasName = !!(row.name || '').trim();
  const hasUuid = !!(row.uuid || '').trim();
  if (!hasName && !hasUuid) { return 'EMPTY_ROW'; }
  if (!hasName) { return 'NAME_EMPTY'; }
  if (!extractTeamUuid(row.uuid)) { return 'UUID_INVALID'; }
  return null;
}

function rowErrorText(error) {
  if (error === 'NAME_EMPTY' || error === 'EMPTY_ROW') {
    return ze('请填写团队名', 'Please enter the team name');
  }
  if (error === 'UUID_INVALID') {
    return ze('UUID 无效，可粘贴 Jira 团队页链接自动解析', 'Invalid UUID; paste the Jira team page URL to parse it');
  }
  return '';
}

function makeRowKey() {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const JiraTeamOptionsModal = ({
  visible,
  appId,
  teamFieldKey,
  value,
  jiraFieldDefaultValue,
  onCancel,
  onSaved,
}) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(MODE_TABLE);
  const [rows, setRows] = useState([]);
  const [csvText, setCsvText] = useState('');
  const [defaultUuid, setDefaultUuid] = useState('');

  useEffect(() => {
    if (!visible) {
      return;
    }
    const currentText = typeof value === 'string' ? value : '';
    setRows(parseTeamRows(currentText));
    setCsvText(currentText);
    setMode(MODE_TABLE); // 始终默认表格模式
    const currentDefault = (jiraFieldDefaultValue || {})[teamFieldKey || ''];
    setDefaultUuid(typeof currentDefault === 'string' ? currentDefault : '');
    // 只依赖 visible：打开弹窗时按当前配置初始化一次即可
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const rowErrors = useMemo(() => {
    const map = Object.create(null);
    rows.forEach((row) => {
      const error = getRowError(row);
      if (error) { map[row.key] = error; }
    });
    return map;
  }, [rows]);

  const duplicateRowKeys = useMemo(() => findDuplicateTeamRowKeys(rows), [rows]);

  // CSV 模式的解析结果
  const { items: csvItems, invalid: csvInvalid, duplicated: csvDuplicated } =
    useMemo(() => parseTeamOptions(csvText), [csvText]);

  const tableHasError = Object.keys(rowErrors).length > 0 || Object.keys(duplicateRowKeys).length > 0;
  const csvHasError = csvInvalid.length > 0 || csvDuplicated.length > 0;
  const hasError = mode === MODE_TABLE ? tableHasError : csvHasError;

  // 保存用的文本：两种模式最终都落 CSV 文本（存储契约不变）
  const saveText = useMemo(() => (
    mode === MODE_TABLE ? toTeamCsvText(rows) : csvText
  ), [mode, rows, csvText]);

  const validItems = useMemo(() => (
    mode === MODE_TABLE
      ? rows.filter((row) => !getRowError(row)).map((row) => ({
          name: (row.name || '').trim(),
          uuid: extractTeamUuid(row.uuid),
        }))
      : csvItems
  ), [mode, rows, csvItems]);

  // 默认团队必须还在有效列表里（配置被编辑过之后原默认值可能已被删掉）
  const effectiveDefaultUuid = validItems.some(x => x.uuid === defaultUuid) ? defaultUuid : '';

  function updateRow(key, field, fieldValue) {
    setRows((prev) => prev.map((row) => (
      row.key === key ? { ...row, [field]: fieldValue } : row
    )));
  }

  /** 粘贴或失焦时把团队页链接归一化成 UUID，避免边输边改打断输入 */
  function normalizeUuidField(key, fieldValue) {
    const parsed = extractTeamUuid(fieldValue);
    if (parsed && parsed !== fieldValue) {
      setRows((prev) => prev.map((row) => (
        row.key === key ? { ...row, uuid: parsed } : row
      )));
    }
  }

  function addRow() {
    setRows((prev) => [...prev, { key: makeRowKey(), name: '', uuid: '', raw: '', error: null }]);
  }

  function removeRow(key) {
    setRows((prev) => prev.filter((row) => row.key !== key));
  }

  function switchToCsv() {
    setCsvText(toTeamCsvText(rows));
    setMode(MODE_CSV);
  }

  function switchToTable() {
    // 非法行不会被丢弃：parseTeamRows 会保留成带原始值的行，用户可在表格里改
    setRows(parseTeamRows(csvText));
    setMode(MODE_TABLE);
  }

  async function onSubmit() {
    const nextDefaults = { ...(jiraFieldDefaultValue || {}) };
    if (teamFieldKey) {
      if (effectiveDefaultUuid) {
        nextDefaults[teamFieldKey] = effectiveDefaultUuid;
      } else {
        // 清空配置 / 未设默认时必须把默认值一起删掉，否则会填一个下拉里已经不存在的 UUID
        delete nextDefaults[teamFieldKey];
      }
    }
    setLoading(true);
    try {
      await dispatch(upsertServerAppSettings(appId, {
        [ServerAppSettings.keys.jiraTeamOptions]: saveText,
        [ServerAppSettings.keys.jiraFieldDefaultValue]: nextDefaults,
      }));
      notification.success({ message: ze('Team选项配置保存成功', 'Team options saved.') });
      onSaved({ text: saveText, defaultUuid: effectiveDefaultUuid });
    } catch (e) {
      notification.error({
        message: ze('Team选项配置保存失败', 'Failed to save team options.'),
        // RestHelper.mustPost 抛的不一定是 Error（5xx / 空响应时是普通对象），description 也要兜上
        description: (e && (e.message || e.description)) || '',
      });
    } finally {
      setLoading(false);
    }
  }

  function onCopyAll() {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(saveText)
        .then(() => notification.success({ message: ze('已复制到剪贴板', 'Copied to clipboard.') }))
        .catch(() => notification.error({ message: ze('复制失败，请手动选中复制', 'Copy failed, please select and copy manually.') }));
      return;
    }
    notification.error({ message: ze('当前浏览器不支持自动复制，请手动选中复制', 'Auto copy is unsupported, please select and copy manually.') });
  }

  const csvColumns = [
    { title: ze('团队名', 'Team Name'), dataIndex: 'name', key: 'name' },
    { title: 'Team UUID', dataIndex: 'uuid', key: 'uuid' },
  ];

  const tableColumns = [
    {
      title: ze('默认', 'Default'),
      key: 'default',
      width: 64,
      align: 'center',
      render: (_, row) => (
        <Radio value={extractTeamUuid(row.uuid) || ''} disabled={!!rowErrors[row.key]} />
      ),
    },
    {
      title: ze('团队名', 'Team Name'),
      key: 'name',
      render: (_, row) => (
        <Input
          value={row.name}
          placeholder={ze('团队名', 'Team name')}
          onChange={(e) => updateRow(row.key, 'name', e.target.value)}
        />
      ),
    },
    {
      title: ze('Team UUID / 团队页链接', 'Team UUID / team page URL'),
      key: 'uuid',
      render: (_, row) => (
        <div>
          <Input
            value={row.uuid}
            placeholder={ze('粘贴 UUID 或 Jira 团队页链接', 'Paste UUID or Jira team page URL')}
            onChange={(e) => updateRow(row.key, 'uuid', e.target.value)}
            onBlur={(e) => normalizeUuidField(row.key, e.target.value)}
            onPaste={(e) => {
              const pasted = (e.clipboardData || window.clipboardData).getData('text');
              if (pasted) { normalizeUuidField(row.key, pasted); }
            }}
          />
          { (rowErrors[row.key] || duplicateRowKeys[row.key]) && (
            <div style={{ color: '#cf1322', fontSize: 12, marginTop: 2 }}>
              { duplicateRowKeys[row.key]
                ? ze('与其他行重复（同名或同 UUID）', 'Duplicated with another row (same name or UUID)')
                : rowErrorText(rowErrors[row.key]) }
            </div>
          ) }
        </div>
      ),
    },
    {
      title: ze('操作', 'Actions'),
      key: 'actions',
      width: 72,
      render: (_, row) => (
        <Button type='link' size='small' onClick={() => removeRow(row.key)}>
          { ze('删除', 'Delete') }
        </Button>
      ),
    },
  ];

  return <Modal
    title={ze('配置 Jira Team 选项', 'Configure Jira Team Options')}
    visible={visible}
    width='760px'
    zIndex={JIRA_TEAM_OPTIONS_MODAL_Z_INDEX}
    maskClosable={false}
    okText={ze('保存', 'Save')}
    okButtonProps={{ disabled: loading || hasError }}
    onCancel={() => onCancel()}
    onOk={() => onSubmit()}
  >
    <div style={{ marginBottom: '12px' }}>
      <Radio.Group
        value={mode}
        onChange={(e) => (e.target.value === MODE_CSV ? switchToCsv() : switchToTable())}
      >
        <Radio.Button value={MODE_TABLE}>{ ze('表格编辑', 'Table') }</Radio.Button>
        <Radio.Button value={MODE_CSV}>{ ze('CSV编辑（批量导入/导出）', 'CSV (bulk)') }</Radio.Button>
      </Radio.Group>
    </div>

    { mode === MODE_TABLE ? (
      <>
        <div style={{ marginBottom: '12px', color: '#888', fontSize: 12 }}>
          { ze(
            'UUID 一列可以直接粘贴 Jira 团队页链接，失焦后会自动解析成 UUID。',
            'You can paste a Jira team page URL into the UUID column; it will be parsed on blur.',
          ) }
        </div>
        <Radio.Group
          value={effectiveDefaultUuid}
          onChange={(e) => setDefaultUuid(e.target.value || '')}
          style={{ width: '100%' }}
        >
          <Table
            size='small'
            pagination={false}
            dataSource={rows}
            columns={tableColumns}
            locale={{ emptyText: ze('暂无配置，点下方「添加一行」开始', 'No option yet, click "Add a row" below') }}
          />
        </Radio.Group>
        <div style={{ marginTop: '8px' }}>
          <Space>
            <Button size='small' onClick={() => addRow()}>{ ze('+ 添加一行', '+ Add a row') }</Button>
            <span style={{ color: '#888', fontSize: 12 }}>
              { ze('共', 'Total') } { validItems.length } { ze('个团队', 'teams') }
              { Object.keys(rowErrors).length > 0 && `，${ze('待修正', 'to fix')} ${Object.keys(rowErrors).length}` }
              { Object.keys(duplicateRowKeys).length > 0 && `，${ze('重复', 'duplicated')} ${Object.keys(duplicateRowKeys).length}` }
            </span>
          </Space>
        </div>
      </>
    ) : (
      <>
        <div style={{ marginBottom: '12px', color: '#888', fontSize: 12 }}>
          { ze(
            '每行一个团队，格式「团队名,Team UUID」。支持从 Excel 直接粘贴（Tab 分隔）、忽略空行与 # 开头的注释行。',
            'One team per line: "team name,Team UUID". Pasting from Excel (tab-separated) works; blank lines and # lines are ignored.',
          ) }
        </div>
        <Input.TextArea
          rows={10}
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          placeholder={PLACEHOLDER}
        />
        <div style={{ margin: '8px 0' }}>
          <Space>
            <Button size='small' onClick={() => onCopyAll()}>
              { ze('复制全部（导出）', 'Copy all (export)') }
            </Button>
            <Button size='small' onClick={() => setCsvText('')}>{ ze('清空', 'Clear') }</Button>
          </Space>
        </div>
        <div style={{ marginBottom: '8px', fontSize: 12 }}>
          { ze('解析结果', 'Parsed result') }：{ ze('有效', 'valid') } { csvItems.length }
          { csvInvalid.length > 0 && `，${ze('无效', 'invalid')} ${csvInvalid.length}` }
          { csvDuplicated.length > 0 && `，${ze('重复', 'duplicated')} ${csvDuplicated.length}` }
        </div>
        { csvInvalid.length > 0 && <Alert
          style={{ marginBottom: '12px' }}
          type='warning'
          showIcon
          message={ze('以下行无效，修正后才能保存（切到表格模式可逐行修改）：', 'These lines are invalid; fix them before saving (switch to table mode to edit row by row):')}
          description={<div>{ csvInvalid.map(x => (
            <div key={x.line} style={{ color: '#cf1322' }}>{ `${ze('第', 'Line')} ${x.line} ${ze('行', '')}：${x.raw}` }</div>
          )) }</div>}
        /> }
        <Table
          size='small'
          pagination={false}
          dataSource={csvItems.map(x => ({ ...x, key: x.uuid }))}
          columns={csvColumns}
          locale={{ emptyText: ze('暂无有效配置', 'No valid option yet') }}
        />
        <div style={{ marginTop: '12px' }}>
          <div style={{ marginBottom: '4px' }}>{ ze('默认团队（可选）', 'Default team (optional)') }</div>
          <Select
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='label'
            placeholder={ze('不设置则每次提单手动选择', 'Leave empty to select manually each time')}
            options={csvItems.map(x => ({ label: x.name, value: x.uuid }))}
            value={effectiveDefaultUuid || undefined}
            onChange={(v) => setDefaultUuid(v || '')}
            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
          />
        </div>
      </>
    ) }

    { hasError && (
      <div style={{ marginTop: '12px', color: '#cf1322', fontSize: 12 }}>
        { ze('存在需要修正的行，修正后才能保存。', 'Some rows need to be fixed before saving.') }
      </div>
    ) }
  </Modal>;
};

JiraTeamOptionsModal.propTypes = {
  visible: PropTypes.bool,
  appId: PropTypes.string,
  teamFieldKey: PropTypes.string,
  value: PropTypes.string,
  jiraFieldDefaultValue: PropTypes.object,
  onCancel: PropTypes.func,
  onSaved: PropTypes.func,
};

export default JiraTeamOptionsModal;
